import { useMemo, useState, createElement } from "react";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import { useHeadlineQueriesCRUD } from "../api/headline-query/useHeadlineQueriesCRUD";
import { useTagsWatchlistsCRUD } from "@/shared/api/pref-storage/tags-watchlists/useTagsWatchlistsCRUD";
import { menuCategories } from "../models/categories";
import { TagsWatchlist } from "@/shared/api/pref-storage/tags-watchlists";
import { FilterMode } from "@/shared/models/filters";
import { FilterMenuConfig } from "@/shared/models/filterTypes";
import {
  ClearSelectionsFooter,
} from "@/shared/components/FilterComponents";
import { isPrebuiltWatchlist } from "@/pages/PortfolioBuilder/prebuiltWatchlists";
import { useFilterPanelMenu } from "@/shared/hooks/useFilterPanelMenu";
import { Portfolio } from "@/types/Portfolio";
import {
  groupWatchlistsByCategory,
  NEWSQUAWK_CATEGORIES,
  NewsquawkCategory,
} from "@/pages/PortfolioBuilder/newsquawkCategories";

interface HeadlinesFilterPanelOptions {
  setActions?: (actions: React.ReactElement[]) => void;
  pushAction?: (action: React.ReactElement) => void;
  persistKey?: string;
  /** Callback when panel opens - use to close other panels */
  onOpen?: () => void;
  closeSearch?: () => void;
  onSearchOpen?: (callback: () => void) => () => void;
}

// Helper to check if a portfolio is ticker-based (has tickers, no keywords)
const isTickerWatchlist = (p: Portfolio): boolean => {
  return p.tickers.length > 0 && (!p.keywords || p.keywords.length === 0);
};

// Helper to check if a portfolio is keyword-based (has keywords, no tickers)
const isKeywordWatchlist = (p: Portfolio): boolean => {
  return (p.keywords?.length ?? 0) > 0 && p.tickers.length === 0;
};

export const useHeadlinesFilterPanel = (options: HeadlinesFilterPanelOptions) => {
  const { portfolios } = usePortfoliosCRUD();
  const { queriesQuery: categoryQueries } = useHeadlineQueriesCRUD();
  const { watchlistsQuery: tagWatchlistsQuery } = useTagsWatchlistsCRUD();
  const [filterMode] = useState<FilterMode>("AND");

  // Define filter configurations
  const filterConfig = useMemo(() => {
    // Separate user and prebuilt watchlists
    const userWatchlists = portfolios.filter((p) => !isPrebuiltWatchlist(p));
    const prebuiltWatchlists = portfolios.filter((p) => isPrebuiltWatchlist(p));

    // Split user watchlists by type
    const userTickerWatchlists = userWatchlists.filter(isTickerWatchlist);
    const userKeywordWatchlists = userWatchlists.filter(isKeywordWatchlist);
    // Mixed watchlists (both tickers and keywords) go in ticker section
    const userMixedWatchlists = userWatchlists.filter(
      (p) => p.tickers.length > 0 && (p.keywords?.length ?? 0) > 0
    );

    // Split prebuilt watchlists by type
    const prebuiltTickerWatchlists = prebuiltWatchlists.filter(isTickerWatchlist);
    const prebuiltKeywordWatchlists = prebuiltWatchlists.filter(isKeywordWatchlist);
    const prebuiltMixedWatchlists = prebuiltWatchlists.filter(
      (p) => p.tickers.length > 0 && (p.keywords?.length ?? 0) > 0
    );

    // Group prebuilt keyword watchlists by category
    const groupedKeywordWatchlists = groupWatchlistsByCategory(prebuiltKeywordWatchlists);

    // All ticker watchlist options (user + prebuilt)
    const allTickerWatchlists = [
      ...userTickerWatchlists,
      ...userMixedWatchlists,
      ...prebuiltTickerWatchlists,
      ...prebuiltMixedWatchlists,
    ];
    const tickerWatchlistOptions = allTickerWatchlists.map((portfolio) => ({
      id: String(portfolio.id),
      label: portfolio.name,
    }));

    // Create subsections for ticker watchlists
    const tickerSubsections: { id: string; label: string; filterIds: string[]; defaultCollapsed: boolean; createNewLink?: { url: string; label?: string } }[] = [];

    // Add user ticker watchlists subsection (always show to allow creating new)
    const userTickerIds = [...userTickerWatchlists, ...userMixedWatchlists].map((p) => String(p.id));
    tickerSubsections.push({
      id: "user-tickers",
      label: "Your Watchlists",
      filterIds: userTickerIds,
      defaultCollapsed: false,
      createNewLink: { url: "/watchlists-filters?create=ticker" },
    });

    // Add Newsquawk ticker watchlists subsection
    const newsquawkTickerIds = [...prebuiltTickerWatchlists, ...prebuiltMixedWatchlists].map(
      (p) => String(p.id)
    );
    if (newsquawkTickerIds.length > 0) {
      tickerSubsections.push({
        id: "newsquawk-tickers",
        label: "Newsquawk Watchlists",
        filterIds: newsquawkTickerIds,
        defaultCollapsed: true,
      });
    }

    // Build keyword watchlist options with category headers for Newsquawk watchlists
    // Category headers are marked with isHeader: true for non-interactive rendering
    const keywordWatchlistOptions: { id: string; label: string; isHeader?: boolean }[] = [];

    // Add user keyword watchlists first (no header needed, they go in their own subsection)
    for (const portfolio of userKeywordWatchlists) {
      keywordWatchlistOptions.push({
        id: String(portfolio.id),
        label: portfolio.name,
      });
    }

    // Add Newsquawk keyword watchlists grouped by category with headers
    for (const categoryConfig of NEWSQUAWK_CATEGORIES) {
      const categoryWatchlists = groupedKeywordWatchlists[categoryConfig.id as NewsquawkCategory];
      if (categoryWatchlists && categoryWatchlists.length > 0) {
        // Add category header as non-interactive label
        keywordWatchlistOptions.push({
          id: `header-${categoryConfig.id.toLowerCase().replace(/\s+/g, "-")}`,
          label: categoryConfig.label,
          isHeader: true,
        });
        // Add watchlists in this category
        for (const portfolio of categoryWatchlists) {
          keywordWatchlistOptions.push({
            id: String(portfolio.id),
            label: portfolio.name,
          });
        }
      }
    }

    // Create subsections for keyword watchlists (just two: user and Newsquawk)
    const keywordSubsections: { id: string; label: string; filterIds: string[]; defaultCollapsed: boolean; createNewLink?: { url: string; label?: string } }[] = [];

    // Add user keywords as first subsection (always show to allow creating new)
    keywordSubsections.push({
      id: "user-keywords",
      label: "Your Watchlists",
      filterIds: userKeywordWatchlists.map((p) => String(p.id)),
      defaultCollapsed: false,
      createNewLink: { url: "/watchlists-filters?create=keyword" },
    });

    // Add all Newsquawk keyword watchlists in one subsection (with category headers inline)
    const allNewsquawkKeywordIds: string[] = [];
    for (const categoryConfig of NEWSQUAWK_CATEGORIES) {
      const categoryWatchlists = groupedKeywordWatchlists[categoryConfig.id as NewsquawkCategory];
      if (categoryWatchlists && categoryWatchlists.length > 0) {
        // Include the header ID
        allNewsquawkKeywordIds.push(`header-${categoryConfig.id.toLowerCase().replace(/\s+/g, "-")}`);
        // Include watchlist IDs
        for (const portfolio of categoryWatchlists) {
          allNewsquawkKeywordIds.push(String(portfolio.id));
        }
      }
    }
    if (allNewsquawkKeywordIds.length > 0) {
      keywordSubsections.push({
        id: "newsquawk-keywords",
        label: "Newsquawk Watchlists",
        filterIds: allNewsquawkKeywordIds,
        defaultCollapsed: true,
      });
    }

    // Tag watchlist options - combine user-created (from API) and virtual Newsquawk tags (from menuCategories)
    const userTagWatchlists = tagWatchlistsQuery.data || [];
    const tagWatchlistOptions: { id: string; label: string; isHeader?: boolean }[] = [];

    // Add user-created tag watchlists
    for (const watchlist of userTagWatchlists) {
      tagWatchlistOptions.push({
        id: String(watchlist.id),
        label: watchlist.name,
      });
    }

    // Add Newsquawk tag watchlists grouped by category with headers
    for (const category of menuCategories) {
      tagWatchlistOptions.push({
        id: `header-tag-${category.title.toLowerCase().replace(/\s+/g, "-")}`,
        label: category.title,
        isHeader: true,
      });
      for (const item of category.items) {
        tagWatchlistOptions.push({
          id: String(item.id),
          label: item.title,
        });
      }
    }

    // Create subsections for tag watchlists
    const tagSubsections: { id: string; label: string; filterIds: string[]; defaultCollapsed: boolean; createNewLink?: { url: string; label?: string } }[] = [];

    tagSubsections.push({
      id: "user-tags",
      label: "Your Watchlists",
      filterIds: userTagWatchlists.map((w) => String(w.id)),
      defaultCollapsed: false,
      createNewLink: { url: "/watchlists-filters?create=tag" },
    });

    const allNewsquawkTagIds: string[] = [];
    for (const category of menuCategories) {
      allNewsquawkTagIds.push(`header-tag-${category.title.toLowerCase().replace(/\s+/g, "-")}`);
      for (const item of category.items) {
        allNewsquawkTagIds.push(String(item.id));
      }
    }
    tagSubsections.push({
      id: "newsquawk-tags",
      label: "Newsquawk Watchlists",
      filterIds: allNewsquawkTagIds,
      defaultCollapsed: true,
    });

    return [
      // Filter sections - Ticker Watchlists
      {
        id: "ticker-watchlists-filter",
        label: "Filter by Ticker Watchlists",
        description: "Filter by stock symbol watchlists",
        filterOptions: tickerWatchlistOptions,
        collapsible: true,
        showCount: true,
        defaultCollapsed: true,
        subsections: tickerSubsections.length > 0 ? tickerSubsections : undefined,
      },
      // Filter sections - Keyword Watchlists
      {
        id: "keyword-watchlists-filter",
        label: "Filter by Keyword Watchlists",
        description: "Filter by keyword watchlists",
        filterOptions: keywordWatchlistOptions,
        collapsible: true,
        showCount: true,
        defaultCollapsed: true,
        subsections: keywordSubsections.length > 0 ? keywordSubsections : undefined,
      },
      // Filter sections - Tag Watchlists
      {
        id: "tag-watchlists-filter",
        label: "Filter by Tag Watchlists",
        description: "Filter headlines by tag collections",
        filterOptions: tagWatchlistOptions,
        collapsible: true,
        showCount: true,
        defaultCollapsed: true,
        subsections: tagSubsections.length > 0 ? tagSubsections : undefined,
      },
      {
        id: "category-filter",
        label: "Advanced Filters",
        description: "Mutually exclusive with basic filters",
        filterOptions:
          categoryQueries.data?.map((query) => ({
            id: String(query.id),
            label: query.queryName,
          })) || [],
        collapsible: true,
        showCount: true,
        defaultCollapsed: true,
        subsections: (() => {
          const queries = categoryQueries.data || [];
          const userIds = queries.filter((q) => q.owner_type === "USER").map((q) => String(q.id));
          const nqIds = queries.filter((q) => q.owner_type === "NEWSQUAWK").map((q) => String(q.id));
          const subs: { id: string; label: string; filterIds: string[]; defaultCollapsed: boolean; createNewLink?: { url: string; label?: string } }[] = [];
          subs.push({
            id: "user-filters",
            label: "Your Filters",
            filterIds: userIds,
            defaultCollapsed: false,
            createNewLink: { url: "/watchlists-filters?create=advanced" },
          });
          if (nqIds.length > 0) {
            subs.push({
              id: "newsquawk-filters",
              label: "Newsquawk Filters",
              filterIds: nqIds,
              defaultCollapsed: true,
            });
          }
          return subs;
        })(),
      },
    ] as FilterMenuConfig[];
  }, [portfolios, categoryQueries.data, tagWatchlistsQuery.data]);

  // Create global footer component with just clear selections button
  const globalFooter = useMemo(
    () => createElement(ClearSelectionsFooter, {}),
    []
  );

  const filterPanel = useFilterPanelMenu(
    filterConfig,
    options.pushAction,
    options.persistKey,
    undefined,
    globalFooter,
    options.onOpen,
    options.closeSearch,
    options.onSearchOpen
  );

  return {
    ...filterPanel,
    filterMode,
    tagWatchlists: [
      ...(tagWatchlistsQuery.data || []),
      ...menuCategories.flatMap((category) =>
        category.items.map((item): TagsWatchlist => ({
          id: item.id,
          name: item.title,
          tags: [String(item.id)],
          owner_type: "NEWSQUAWK",
        }))
      ),
    ],
  };
};
